<template>
  <transition name="el-fade-in-linear">
    <div v-if="visible && message" class="brand-confusion-tip">
      <div class="brand-confusion-tip__head">
        <span class="brand-confusion-tip__title">
          <i class="el-icon-warning"></i>
          严重违规：混淆品牌信息
        </span>
        <span class="brand-confusion-tip__close el-icon-close" title="关闭" @click="close"></span>
      </div>
      <div class="brand-confusion-tip__body">{{ message }}</div>
      <div v-if="sampleLines.length" class="brand-confusion-tip__samples">
        <div v-for="(line, idx) in sampleLines" :key="idx" class="brand-confusion-tip__sample">{{ line }}</div>
      </div>
      <div class="brand-confusion-tip__foot">
        <el-button type="text" size="mini" @click="goList">立即查看</el-button>
      </div>
    </div>
  </transition>
</template>

<script>
const POLL_MS = 2 * 60 * 1000;
const KEYWORD = "发布混淆信息";

export default {
  name: "BrandConfusionTip",
  data() {
    return {
      visible: false,
      message: "",
      sampleLines: [],
      _pendingIds: [],
      _pollTimer: null,
    };
  },
  mounted() {
    this.check();
    this._pollTimer = setInterval(() => this.check(), POLL_MS);
  },
  beforeDestroy() {
    if (this._pollTimer) {
      clearInterval(this._pollTimer);
      this._pollTimer = null;
    }
  },
  methods: {
    storageKey() {
      const user = this.$api.getUserInfo() || {};
      const uid = user.sub_master_id || user.master_id || "0";
      return `brandConfusionTipDismissed_${uid}`;
    },
    getDismissedIds() {
      try {
        const raw = localStorage.getItem(this.storageKey());
        const arr = raw ? JSON.parse(raw) : [];
        return new Set(Array.isArray(arr) ? arr.map(String) : []);
      } catch (e) {
        return new Set();
      }
    },
    saveDismissedIds(ids) {
      const set = this.getDismissedIds();
      (ids || []).forEach((id) => {
        if (id) set.add(String(id));
      });
      // 控制体积，只保留最近 500 条
      const list = [...set].slice(-500);
      localStorage.setItem(this.storageKey(), JSON.stringify(list));
    },
    buildMessage(total, shops, brands, days) {
      if (!total) return "";
      const parts = [`近 ${days || 7} 天检测到 ${total} 条「混淆品牌/发布混淆信息」严重违规`];
      if (shops.length) {
        const shown = shops.slice(0, 3).join("、");
        parts.push(shops.length > 3 ? `涉及店铺：${shown}等 ${shops.length} 个店铺` : `涉及店铺：${shown}`);
      }
      if (brands.length) {
        parts.push(`涉及品牌：${brands.slice(0, 5).join("、")}`);
      }
      parts.push("请尽快处理，避免加重处罚");
      return parts.join("。") + "。";
    },
    buildSampleLines(items) {
      return (items || []).slice(0, 3).map((it) => {
        const shop = it.shop_name || "未知店铺";
        const brand = it.brand ? `【${it.brand}】` : "";
        const reason = it.violation_reason || "发布混淆信息";
        const level = it.circumstances_text ? `（${it.circumstances_text}）` : "";
        return `${shop}${brand}：${reason}${level}`;
      });
    },
    async check() {
      try {
        const res = await this.$call.invoke("SqliteDB.excute", {
          action: "getBrandConfusionViolationSummary",
          data: { days: 7 },
        });
        if (res?.code !== 1 || !res.data?.total) {
          this.visible = false;
          this.message = "";
          this.sampleLines = [];
          this._pendingIds = [];
          return;
        }
        const summary = res.data;
        const dismissed = this.getDismissedIds();
        const freshItems = (summary.items || []).filter((it) => it.ticket_id && !dismissed.has(String(it.ticket_id)));
        if (!freshItems.length) {
          this.visible = false;
          this.message = "";
          this.sampleLines = [];
          this._pendingIds = [];
          return;
        }
        this._pendingIds = freshItems.map((it) => String(it.ticket_id));
        const shops = [...new Set(freshItems.map((i) => i.shop_name).filter(Boolean))];
        const brands = [...new Set(freshItems.map((i) => i.brand).filter(Boolean))];
        const showTotal = summary.total > freshItems.length ? summary.total : freshItems.length;
        this.message = this.buildMessage(
          showTotal,
          shops.length ? shops : summary.shops || [],
          brands.length ? brands : summary.brands || [],
          summary.days
        );
        this.sampleLines = this.buildSampleLines(freshItems);
        this.visible = !!this.message;
      } catch (e) {
        /* ignore */
      }
    },
    close() {
      this.saveDismissedIds(this._pendingIds);
      this.visible = false;
      this.message = "";
      this.sampleLines = [];
      this._pendingIds = [];
    },
    goList() {
      this.close();
      const query = { keyword: KEYWORD };
      if (this.$route.name !== "admin_violation_ticket") {
        this.$router.push({ name: "admin_violation_ticket", query });
      } else {
        this.$router.replace({ name: "admin_violation_ticket", query }).catch(() => {});
      }
    },
  },
};
</script>

<style scoped>
.brand-confusion-tip {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 5000;
  max-width: 360px;
  padding: 12px 14px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 6px 22px rgba(245, 108, 108, 0.28);
  border-left: 4px solid #f56c6c;
}

.brand-confusion-tip__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.brand-confusion-tip__title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  font-size: 15px;
  color: #c45656;
}

.brand-confusion-tip__title i {
  color: #f56c6c;
  font-size: 18px;
}

.brand-confusion-tip__close {
  cursor: pointer;
  font-size: 16px;
  color: #909399;
  padding: 2px;
  line-height: 1;
}

.brand-confusion-tip__close:hover {
  color: #606266;
}

.brand-confusion-tip__body {
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
}

.brand-confusion-tip__samples {
  margin-top: 8px;
  padding: 8px 10px;
  background: #fef0f0;
  border-radius: 6px;
}

.brand-confusion-tip__sample {
  font-size: 12px;
  color: #a44;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.brand-confusion-tip__sample + .brand-confusion-tip__sample {
  margin-top: 4px;
}

.brand-confusion-tip__foot {
  margin-top: 8px;
  text-align: right;
}

.brand-confusion-tip__foot .el-button {
  color: #f56c6c;
  font-weight: 600;
}
</style>
